import React from "react";
import { Link } from "wouter";
import { Music, MapPin, Compass } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { FESTIVAL_CATEGORY_CONFIG } from "@/lib/category-config";
import type { FestivalCategory } from "@/types/festival";
import type { SearchEntry } from "@/lib/search-index";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getDetailHref(entry: SearchEntry): string {
  if (entry.type === "festival") {
    return `/festival-guide?festival=${encodeURIComponent(entry.id)}`;
  }
  return `/route/${encodeURIComponent(entry.id)}`;
}

function getTypeIcon(type: SearchEntry["type"]) {
  switch (type) {
    case "festival":
      return Music;
    case "journey":
      return Compass;
    default:
      return MapPin;
  }
}

function getCategoryBadgeClass(category: string): string {
  const cfg = FESTIVAL_CATEGORY_CONFIG[category as FestivalCategory];
  return cfg?.color ?? "bg-muted text-muted-foreground";
}

// ---------------------------------------------------------------------------
// Result card per type
// ---------------------------------------------------------------------------

interface ResultItemProps {
  entry: SearchEntry;
  onClick: () => void;
}

export const SearchResultItem = React.memo(function SearchResultItem({ entry, onClick }: ResultItemProps) {
  const Icon = getTypeIcon(entry.type);

  return (
    <Link
      href={getDetailHref(entry)}
      onClick={onClick}
      className="flex items-start gap-3 px-4 py-3 hover:bg-accent transition-colors cursor-pointer"
    >
      <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate">{entry.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground truncate">{entry.location}</span>
          <Badge
            className={`shrink-0 text-[10px] px-1.5 py-0 h-4 ${getCategoryBadgeClass(entry.category)}`}
          >
            {entry.category}
          </Badge>
        </div>
      </div>
    </Link>
  );
});
